import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { CINEMA_ON_SURFACE, CINEMA_PRIMARY, ghostBorderSx, glassCardSx } from '../WatchPage/watchCinemaTheme';

interface IViewToggler {
    variant?: 'default' | 'cinema';
}

const cinemaMenuPaperSx = {
    background: 'rgba(32, 31, 31, 0.96)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 198, 62, 0.2)',
    borderRadius: 2,
    mt: 1,
    '& .MuiMenuItem-root': {
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 14,
        color: CINEMA_ON_SURFACE,
    },
    '& .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(255, 198, 62, 0.1)',
    },
    '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(255, 198, 62, 0.15)',
        color: CINEMA_PRIMARY,
    },
    '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: 'rgba(255, 198, 62, 0.2)',
    },
};

const ViewToggler: React.FC<IViewToggler> = ({ variant = 'default' }) => {
    const { toggleView, view, themeStyle } = useThemeContext();

    const handleViewChange = (event: SelectChangeEvent) => {
        toggleView(event.target.value);
        localStorage.setItem('view', event.target.value);
    };

    if (variant === 'cinema') {
        return (
            <Box
                sx={{
                    ...glassCardSx,
                    ...ghostBorderSx,
                    borderRadius: 2,
                    px: { xs: 1, sm: 1.25 },
                    py: 0.25,
                    minWidth: { xs: 100, sm: 132 },
                    flexShrink: 0,
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiOutlinedInput-root': {
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: `1px solid ${CINEMA_PRIMARY}`,
                        },
                    },
                    '& .MuiSelect-select': {
                        py: { xs: 0.85, sm: 1 },
                        pr: '28px !important',
                        pl: 1,
                        display: 'flex',
                        alignItems: 'center',
                        color: CINEMA_ON_SURFACE,
                        fontSize: { xs: 12, sm: 14 },
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                    },
                    '& .MuiSvgIcon-root': {
                        color: `${CINEMA_ON_SURFACE}b3`,
                    },
                }}>
                <Select
                    name="viewToggler"
                    variant="outlined"
                    value={view}
                    onChange={handleViewChange}
                    MenuProps={{
                        PaperProps: { sx: cinemaMenuPaperSx },
                        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                        transformOrigin: { vertical: 'top', horizontal: 'right' },
                    }}>
                    <MenuItem value="grid">Grid</MenuItem>
                    <MenuItem value="table">Table</MenuItem>
                </Select>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                '.MuiOutlinedInput-root': {
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                },
                '.MuiSvgIcon-root': {
                    color: theme[themeStyle].tag_white,
                },
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
                '.MuiSelect-select': { p: 0 },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 0,
                },
            }}>
            <Select
                name="viewToggler"
                sx={{
                    color: theme[themeStyle].tag_white,
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            backgroundColor: theme[themeStyle].tertiary_white,
                            color: theme[themeStyle].tag_white,
                            '& .MuiButtonBase-root:hover': {
                                backgroundColor: theme[themeStyle].role_hover,
                            },
                            '& .Mui-selected': {
                                backgroundColor: theme[themeStyle].selected_grey_orange,
                            },
                        },
                    },
                }}
                value={view}
                onChange={handleViewChange}>
                <MenuItem sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }} value="grid">
                    Grid
                </MenuItem>
                <MenuItem sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }} value="table">
                    Table
                </MenuItem>
            </Select>
        </Box>
    );
};

export { ViewToggler };
